import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Tooltip } from '@mui/material';
import DataTable, { ColumnDef } from '@/components/utils/DataTable';
import { DateRangeFilter, FilterOption } from '@/components/utils/filters';
import { Button, FilterAsyncMultiSelect, FilterMultiSelect, TabBar, signedClass } from '@/components/ui';
import { contributionAnalysisApi, schemesApi } from '@/services/api';
import { useClientApi } from '@/hooks/useClientApi';
import { useHoldingsDateRestriction } from '@/hooks/useHoldingsDateRestriction';
import { ContributionRow, ContributionDataError } from '@/types/client';
import { formatCurrencyInCrores, formatPercentage, formatSchemeLabel } from '@/utils/formatters';
import { sumBy } from '@/utils/aggregate';
import shared from '@/components/utils/shared.module.css';

const EXCLUDED_SCHEME_CODES = new Set(['SCPL', 'SIML', 'SIDI']);

type FilterMode = 'scheme' | 'client';
type SortDir = 'asc' | 'desc';

const CRORE = 1e7;

const signedCr = (value: number): React.ReactNode => (
  <span className={signedClass(value) || undefined}>
    {formatCurrencyInCrores(value, undefined, true)}
  </span>
);

const signedPct = (fraction: number): React.ReactNode => (
  <span className={signedClass(fraction) || undefined}>
    {formatPercentage(fraction, 2, { bare: true, fromFraction: true })}
  </span>
);

/** The stock price return is null for holdings with no price history (cash, receivables, unlisted). */
const optionalSignedPct = (fraction: number | null): React.ReactNode =>
  fraction === null ? '—' : signedPct(fraction);

/** Format a Date as a local YYYY-MM-DD string (timezone-safe). */
const toIsoDate = (date: Date): string => {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

/** First and last calendar day of the previous month. */
const getPreviousMonthRange = (): { start: string; end: string } => {
  const now = new Date();
  const previousMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0); // day 0 = last day of prev month
  const previousMonthStart = new Date(previousMonthEnd.getFullYear(), previousMonthEnd.getMonth(), 1);
  return { start: toIsoDate(previousMonthStart), end: toIsoDate(previousMonthEnd) };
};

const ContributionAnalysis: React.FC = () => {
  const previousMonth = useMemo(getPreviousMonthRange, []);
  const [startDate, setStartDate] = useState(previousMonth.start);
  const [endDate, setEndDate] = useState(previousMonth.end);
  const [mode, setMode] = useState<FilterMode>('scheme');
  const [selectedSchemes, setSelectedSchemes] = useState<FilterOption[]>([]);
  const [selectedClients, setSelectedClients] = useState<FilterOption[]>([]);

  const { getClients } = useClientApi();
  const { accessCutoffDate, isDateRestricted, clampDate } = useHoldingsDateRestriction();

  // Re-clamp both bounds when the restriction (re)loads, in case a too-recent
  // default was set before the access ceiling was known.
  useEffect(() => {
    setStartDate(prev => (isDateRestricted(prev) ? clampDate(prev) : prev));
    setEndDate(prev => (isDateRestricted(prev) ? clampDate(prev) : prev));
  }, [accessCutoffDate, isDateRestricted, clampDate]);

  const loadFilteredSchemeOptions = useCallback(async (inputValue: string): Promise<FilterOption[]> => {
    try {
      const response = await schemesApi.getSchemes({ search: inputValue, page_size: 50, is_active: true, ordering: 'code' });
      return response.results
        .filter(s => !s.code || !EXCLUDED_SCHEME_CODES.has(s.code))
        .map(s => ({ value: s.id.toString(), label: formatSchemeLabel(s, { includeAcronym: false }) }));
    } catch {
      return [];
    }
  }, []);

  const loadFilteredClientOptions = useCallback(async (inputValue: string): Promise<FilterOption[]> => {
    try {
      const response = await getClients({ search: inputValue, page_size: 500 });
      // Client list exposes only `scheme_name` ("<display name> - <code>"), no
      // scheme id — so exclude by matching the trailing " - <code>" suffix.
      const isExcludedScheme = (schemeName?: string): boolean =>
        !!schemeName && [...EXCLUDED_SCHEME_CODES].some(code => schemeName.endsWith(` - ${code}`));
      return response.results
        .filter(c => !isExcludedScheme(c.scheme_name))
        .map(c => ({
          value: c.id.toString(),
          label: `${c.client_code} - ${c.full_name || `${c.first_name} ${c.last_name}`}`,
        }));
    } catch {
      return [];
    }
  }, [getClients]);

  const [rows, setRows] = useState<ContributionRow[]>([]);
  const [dataErrors, setDataErrors] = useState<ContributionDataError[]>([]);
  const [portfolioReturn, setPortfolioReturn] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSectors, setSelectedSectors] = useState<FilterOption[]>([]);
  const [sortField, setSortField] = useState<string>('contribution');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [showErrors, setShowErrors] = useState(false);
  const [showDetailCols, setShowDetailCols] = useState(false);

  const hasValidSelection =
    !!startDate && !!endDate &&
    (mode === 'scheme' ? selectedSchemes.length > 0 : selectedClients.length > 0);

  const handleApplyFilters = useCallback(async () => {
    if (!hasValidSelection) return;
    // Defense-in-depth: clampDate and the useEffect snap normally keep both
    // bounds under the ceiling, but mirror the backend restriction here too.
    if (isDateRestricted(endDate) || isDateRestricted(startDate)) {
      setError(`Data after ${accessCutoffDate} requires complete access.`);
      setRows([]);
      setDataErrors([]);
      setPortfolioReturn(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await contributionAnalysisApi.getContributionAnalysis({
        from_date: startDate,
        to_date: endDate,
        ...(mode === 'scheme'
          ? { scheme_ids: selectedSchemes.map(s => s.value).join(',') }
          : { client_ids: selectedClients.map(c => c.value).join(',') }),
      });
      // Mirror the backend's restriction; accessCutoffDate may be null if the
      // frontend restriction API failed, so fall back to a generic phrase.
      if (response.access_restricted) {
        setError(`Data after ${accessCutoffDate ?? 'the permitted date'} requires complete access.`);
        setRows([]);
        setSelectedSectors([]);
        setDataErrors([]);
        setPortfolioReturn(null);
        return;
      }
      setRows(response.results);
      setSelectedSectors([]);
      setDataErrors(response.data_errors ?? []);
      setPortfolioReturn(response.portfolio_return ?? null);
    } catch (err: any) {
      console.error('Error loading contribution analysis:', err);
      setError(err.response?.data?.message || 'Failed to load contribution analysis.');
      setRows([]);
      setDataErrors([]);
      setPortfolioReturn(null);
    } finally {
      setLoading(false);
    }
  }, [hasValidSelection, startDate, endDate, mode, selectedSchemes, selectedClients, isDateRestricted, accessCutoffDate]);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  // Sector options derived from the sectors present in the loaded rows.
  const sectorOptions = useMemo<FilterOption[]>(() => {
    const sectors = new Set<string>();
    rows.forEach(r => { if (r.sector_name) sectors.add(r.sector_name); });
    return [...sectors]
      .sort((a, b) => a.localeCompare(b))
      .map(sector => ({ value: sector, label: sector }));
  }, [rows]);

  const visibleRows = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const selectedSectorSet = new Set(selectedSectors.map(s => s.value));
    const filtered = rows.filter(r => {
      const matchesQuery = !query ||
        r.symbol?.toLowerCase().includes(query) || r.name?.toLowerCase().includes(query);
      const matchesSector = selectedSectorSet.size === 0 || selectedSectorSet.has(r.sector_name);
      return matchesQuery && matchesSector;
    });
    const sorted = [...filtered].sort((a, b) => {
      const av = a[sortField as keyof ContributionRow];
      const bv = b[sortField as keyof ContributionRow];
      // stock_price_return is the only nullable field: its nulls ("no price history")
      // sort last in either direction rather than interleaving with real returns.
      // Deliberately scoped to that field — an unexpected null in a column the backend
      // guarantees as a number should fall through to the comparisons below and show up
      // as a visibly wrong row, not be quietly buried at the bottom of the table.
      if (sortField === 'stock_price_return' && (av === null || bv === null)) {
        if (av === null && bv === null) return 0;
        return av === null ? 1 : -1;
      }
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      const cmp = String(av ?? '').localeCompare(String(bv ?? ''));
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [rows, searchQuery, selectedSectors, sortField, sortDir]);

  const handleExportCsv = useCallback(() => {
    if (visibleRows.length === 0) return;

    // Raw numeric values (Cr and %), no locale grouping, for a machine-readable file.
    const crVal = (value: number): string => (value / CRORE).toFixed(2);
    const pctVal = (fraction: number): string => (fraction * 100).toFixed(2);
    const holdingLabel = (row: ContributionRow): string =>
      /^\d+$/.test(row.symbol ?? '') ? (row.name ?? '') : (row.symbol ?? '');

    const headers = [
      'Holding',
      'Sector',
      'Beginning MV (Cr)', 'Ending MV (Cr)',
      'Beginning Asset (%)', 'Ending Asset (%)',
      'Purchases (Cr)', 'Sales (Cr)',
      'Realized Gain (Cr)', 'Unrealized Gain (Cr)', 'Income (Cr)',
      'Total Gain/Loss (Cr)',
      'Contribution (%)',
      'Position Return (%)',
      'Stock Price Return (%)',
    ];

    const bodyRows = visibleRows.map(row => [
      holdingLabel(row),
      row.sector_name ?? '',
      crVal(row.beginning_mv), crVal(row.ending_mv),
      pctVal(row.beginning_asset_pct), pctVal(row.ending_asset_pct),
      crVal(row.purchases), crVal(row.sales),
      crVal(row.realized_gain), crVal(row.unrealized_gain), crVal(row.income),
      crVal(row.total_gain_loss),
      pctVal(row.contribution),
      pctVal(row.position_return),
      row.stock_price_return === null ? '' : pctVal(row.stock_price_return),
    ]);


    const csvContent = [headers, ...bodyRows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob(['﻿', csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `contribution_analysis_${startDate}_to_${endDate}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [visibleRows, startDate, endDate]);

  const crFooter = (key: keyof ContributionRow) =>
    (footerRows: ContributionRow[]) => formatCurrencyInCrores(sumBy(footerRows, key), undefined, true);
  const signedCrFooter = (key: keyof ContributionRow) =>
    (footerRows: ContributionRow[]) => signedCr(sumBy(footerRows, key));
  const signedPctFooter = (key: keyof ContributionRow) =>
    (footerRows: ContributionRow[]) => signedPct(sumBy(footerRows, key));

  const columns: ColumnDef<ContributionRow>[] = useMemo(() => {
    const detailCols: ColumnDef<ContributionRow>[] = [
      {
        key: 'purchases', header: 'Purchases (₹ Cr)', sortable: true, align: 'right',
        render: row => formatCurrencyInCrores(row.purchases, undefined, true),
        renderFooter: crFooter('purchases'),
      },
      {
        key: 'sales', header: 'Sales (₹ Cr)', sortable: true, align: 'right',
        render: row => formatCurrencyInCrores(row.sales, undefined, true),
        renderFooter: crFooter('sales'),
      },
      {
        key: 'realized_gain', header: 'Realized Gain (₹ Cr)', sortable: true, align: 'right',
        render: row => signedCr(row.realized_gain),
        renderFooter: signedCrFooter('realized_gain'),
      },
      {
        key: 'unrealized_gain', header: 'Unrealized Gain (₹ Cr)', sortable: true, align: 'right',
        render: row => signedCr(row.unrealized_gain),
        renderFooter: signedCrFooter('unrealized_gain'),
      },
      {
        key: 'income', header: 'Income (₹ Cr)', sortable: true, align: 'right',
        render: row => signedCr(row.income),
        renderFooter: signedCrFooter('income'),
      },
    ];

    return [
      {
        key: 'symbol', header: 'Holding', sortable: true, align: 'left',
        render: row => {
        const isNumeric = /^\d+$/.test(row.symbol ?? '');
        return (
          <Tooltip title={isNumeric ? row.symbol : row.name} arrow placement="top">
            <span>{isNumeric ? row.name : row.symbol}</span>
          </Tooltip>
        );
      },
        renderFooter: () => <strong>Total</strong>,
      },
      {
        key: 'sector_name', header: 'Sector', sortable: true, align: 'left',
        render: row => row.sector_name || '—',
      },
      {
        key: 'beginning_mv', header: 'Beginning MV (₹ Cr)', sortable: true, align: 'right',
        render: row => formatCurrencyInCrores(row.beginning_mv, undefined, true),
        renderFooter: crFooter('beginning_mv'),
      },
      {
        key: 'ending_mv', header: 'Ending MV (₹ Cr)', sortable: true, align: 'right',
        render: row => formatCurrencyInCrores(row.ending_mv, undefined, true),
        renderFooter: crFooter('ending_mv'),
      },
      {
        key: 'beginning_asset_pct', header: 'Beginning Asset (%)', sortable: true, align: 'right',
        render: row => formatPercentage(row.beginning_asset_pct, 2, { bare: true, fromFraction: true }),
        renderFooter: footerRows => formatPercentage(sumBy(footerRows, 'beginning_asset_pct'), 2, { bare: true, fromFraction: true }),
      },
      {
        key: 'ending_asset_pct', header: 'Ending Asset (%)', sortable: true, align: 'right',
        render: row => formatPercentage(row.ending_asset_pct, 2, { bare: true, fromFraction: true }),
        renderFooter: footerRows => formatPercentage(sumBy(footerRows, 'ending_asset_pct'), 2, { bare: true, fromFraction: true }),
      },
      ...(showDetailCols ? detailCols : []),
      {
        key: 'total_gain_loss', header: 'Total Gain/Loss (₹ Cr)', sortable: true, align: 'right',
        render: row => signedCr(row.total_gain_loss),
        renderFooter: signedCrFooter('total_gain_loss'),
      },
      {
        key: 'contribution',
        header: 'Contribution (%)',
        tooltip: 'Σ F · (daily gain ÷ prior day AUM), F chained daily',
        sortable: true, align: 'right',
        render: row => signedPct(row.contribution),
        renderFooter: signedPctFooter('contribution'),
      },
      // No footer on either return column: they are per-holding rates, so summing
      // them across rows would be meaningless.
      {
        key: 'position_return',
        header: 'Position Return (%)',
        tooltip: 'What the portfolio earned on this holding: ∏(1 + daily gain ÷ prior day position MV) − 1, independent of its weight',
        sortable: true, align: 'right',
        render: row => signedPct(row.position_return),
      },
      {
        key: 'stock_price_return',
        header: 'Stock Price Return (%)',
        tooltip: 'The stock’s own move: close on the day before the start date → close on the end date (corporate-action adjusted), ignoring when we traded it',
        sortable: true, align: 'right',
        render: row => optionalSignedPct(row.stock_price_return),
      },
    ];
  }, [showDetailCols]);

  const filterSlot = (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end', width: '100%' }}>
      {/* No `min`: the lookback is unbounded; only the upper bound is capped to
          the date-access ceiling. */}
      <DateRangeFilter
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={date => setStartDate(clampDate(date))}
        onEndDateChange={date => setEndDate(clampDate(date))}
        max={accessCutoffDate ?? undefined}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-dim)', textTransform: 'uppercase' }}>
          Filter By
        </span>
        <TabBar
          ariaLabel="Filter by"
          value={mode}
          onChange={next => setMode(next as FilterMode)}
          tabs={[
            { value: 'scheme', label: 'Scheme' },
            { value: 'client', label: 'Client' },
          ]}
        />
      </Box>
      <Box sx={{ width: 300, '& > div > div': { maxWidth: 'none', width: '100%' } }}>
        <div style={{ display: mode === 'scheme' ? 'block' : 'none' }}>
          <FilterAsyncMultiSelect
            label="Schemes"
            value={selectedSchemes}
            onChange={setSelectedSchemes}
            loadOptions={loadFilteredSchemeOptions}
            placeholder="Select schemes…"
          />
        </div>
        <div style={{ display: mode === 'client' ? 'block' : 'none' }}>
          <FilterAsyncMultiSelect
            label="Clients"
            value={selectedClients}
            onChange={setSelectedClients}
            loadOptions={loadFilteredClientOptions}
            placeholder="Select clients…"
          />
        </div>
      </Box>
      <Button
        variant="primary"
        onClick={handleApplyFilters}
        disabled={!hasValidSelection || loading}
      >
        {loading ? 'Loading…' : 'Apply Filters'}
      </Button>
    </Box>
  );

  const returnBadge = (label: string, tooltip: string, value: number | null) =>
    value === null ? null : (
      <Tooltip title={tooltip} arrow placement="top">
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8, padding: '6px 14px', cursor: 'default' }}>
          <span style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500 }}>{label}:</span>
          <span className={signedClass(value) || undefined} style={{ fontSize: 14, fontWeight: 700 }}>
            {formatPercentage(value * 100)}
          </span>
        </div>
      </Tooltip>
    );

  const hasPortfolioReturns = portfolioReturn !== null;

  const searchRowSlot = rows.length > 0 ? (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      <div style={{ minWidth: 220 }}>
        <FilterMultiSelect
          label="Sectors"
          value={selectedSectors}
          onChange={next => setSelectedSectors(next)}
          options={sectorOptions}
        />
      </div>
      <Button size="sm" onClick={() => setShowDetailCols(prev => !prev)}>
        {showDetailCols ? '▲ Hide breakdown' : '▼ Show breakdown'}
      </Button>
      <Button size="sm" onClick={handleExportCsv} disabled={visibleRows.length === 0}>
        ⬇ Export CSV
      </Button>
    </div>
  ) : undefined;

  const toolbarSlot = (hasPortfolioReturns || dataErrors.length > 0) ? (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {hasPortfolioReturns && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignSelf: 'flex-start' }}>
          {returnBadge('Portfolio Return (TWR)', '∏(1 + daily return) − 1, where daily return = (today AUM − prev AUM − ext. flow) ÷ prev AUM', portfolioReturn)}
        </div>
      )}
      {dataErrors.length > 0 && <div style={{ border: '1px solid var(--neg-line)', background: 'var(--neg-soft)', borderRadius: 8, padding: '10px 14px' }}>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setShowErrors(prev => !prev)}
        style={{ color: 'var(--neg)' }}
      >
        ⚠ {dataErrors.length} Data Error{dataErrors.length === 1 ? '' : 's'} — holding quantity changes not explained by trades {showErrors ? '▲' : '▼'}
      </Button>
      {showErrors && (
        <div style={{ maxHeight: 240, overflow: 'auto', marginTop: 10 }}>
          <table className={shared.table} style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--text-dim)' }}>
                <th style={{ padding: '4px 8px' }}>Date</th>
                <th style={{ padding: '4px 8px' }}>Client</th>
                <th style={{ padding: '4px 8px' }}>Holding</th>
                <th style={{ padding: '4px 8px', textAlign: 'right' }}>From Holdings</th>
                <th style={{ padding: '4px 8px', textAlign: 'right' }}>From Transactions</th>
                <th style={{ padding: '4px 8px', textAlign: 'right' }}>Missing Qty</th>
              </tr>
            </thead>
            <tbody>
              {dataErrors.map((errorRow, index) => (
                <tr key={`${errorRow.client_code}-${errorRow.symbol}-${errorRow.holding_date}-${index}`}>
                  <td style={{ padding: '4px 8px' }}>{errorRow.holding_date}</td>
                  <td style={{ padding: '4px 8px' }}>{errorRow.client_code}</td>
                  <td style={{ padding: '4px 8px' }} title={errorRow.name}>{errorRow.symbol}</td>
                  <td style={{ padding: '4px 8px', textAlign: 'right' }}>{errorRow.qty_diff}</td>
                  <td style={{ padding: '4px 8px', textAlign: 'right' }}>{errorRow.traded_qty}</td>
                  <td style={{ padding: '4px 8px', textAlign: 'right' }}>{errorRow.missing_qty}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>}
    </div>
  ) : undefined;

  return (
    <DataTable<ContributionRow>
      title="Contribution Analysis"
      data={visibleRows}
      columns={columns}
      rowKey={row => row.company_id}
      totalCount={visibleRows.length}
      loading={loading}
      error={error}
      currentPage={1}
      totalPages={1}
      onPageChange={() => undefined}
      pageSize={Math.max(visibleRows.length, 1)}
      sortField={sortField}
      sortDir={sortDir}
      onSort={handleSort}
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      searchPlaceholder="Search by symbol or company…"
      filterSlot={filterSlot}
      toolbarSlot={toolbarSlot}
      searchRowSlot={searchRowSlot}
      compact
      fitContent
      emptyMessage={
        hasValidSelection
          ? 'Press “Apply Filters” to load contribution analysis for the selected period.'
          : 'Select a date range and at least one scheme (or a client), then press “Apply Filters”.'
      }
    />
  );
};

export default ContributionAnalysis;
